import { useSnackBarContext } from '@/utils/snackbar';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';

export default function usePost(endpoint, config = {}) {
  const [pinging, setPinging] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [pingResponse, setPingResponse] = useState({});
  const [pingError, setPingError] = useState(null);

  const { updateSnack } = useSnackBarContext();

  let fullEndpoint = '/api' + endpoint;

  const mutation = useMutation((data) => {
    setRequestData(data);
    return axios.post(fullEndpoint, data);
  });

  // Handle error message.
  useEffect(() => {
    if (!mutation.isError && !pingError) return;
    updateSnack({
      severity: 'error',
      message: config?.errorMessage || mutation.error.message,
    });
  }, [mutation.isError, pingError]);

  // Handle success message.
  useEffect(() => {
    if (!pinging && !pingError && mutation.isSuccess) {
      if (config?.successMessage) {
        updateSnack({
          severity: 'success',
          message: config.successMessage,
        });
      }
    }
  }, [pinging, mutation.isSuccess]);

  // If this is a method the requires pinging for updates, invoke whenever we invoke
  // mutation.
  useEffect(() => {
    if (!config.ping) return;

    // Triggers everytime we fire main request.
    if (mutation.isLoading) {
      setPinging(true);
      setPingError(null);
    }

    // Triggers every time we ping.
    if (pinging && !mutation.isLoading) pingAndWait();
  }, [mutation.isLoading]);

  // Cancel ping if something breaks.
  useEffect(() => setPinging(false), [mutation.error]);

  // Endlessly make the same ping request.
  function pingAndWait() {
    if (!pinging) return;
    axios
      .post(fullEndpoint + '?ping=true', requestData)
      .then((resp) => {
        // Store ping data.
        setPingResponse(resp.data);

        // If we're not done, ping again.
        if (resp.data.completed) {
          setPinging(false);
        } else setTimeout(() => pingAndWait(requestData), 3000);
      })
      .catch((err) => {
        setPingError(err);
        setPinging(false);
      });
  }

  return {
    request: mutation,
    fire: mutation.mutate,
    fireAsync: mutation.mutateAsync,
    isLoading: pinging || mutation.isLoading,
    isError: mutation.isError || pingError,
    data: mutation.data?.data?.body || mutation.data?.data,
    error: mutation.error || pingError,
    reset: mutation.reset,
    isSuccess: !pinging && mutation.isSuccess,
    status: mutation.status,
    pingAndWait,
    pingResponse,
  };
}
